// pages/dashboard/components/Header.jsx
// ─────────────────────────────────────────────────────────────
// CHANGES FROM YOUR VERSION:
//
// 1. NAV_LABELS — added missing entries so the page title renders
//    correctly for every view. Previously "tickets", "providers",
//    "coupon", "marketing_slides", "catalog", "companies",
//    "savings_settings", "reward_rules", "fraud", "provider_balances",
//    "audit_logs" all fell through to "Dashboard".
//
// 2. onMenuToggle — now optional (AdminLayout passes it only on
//    mobile). Added a default no-op so the component doesn't crash
//    if called without it.
//
// Everything else is UNCHANGED.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../context/useAuth";

// Complete nav ID → display label map.
// Must stay in sync with NAV_GROUPS in Sidebar.jsx.
const NAV_LABELS = {
  dashboard:        "Dashboard",
  analytics:        "Analytics",
  transactions:     "Transactions",
  users:            "Users",
  kyc:              "KYC Verification",
  assets:           "Assets & Rates",
  wallets:          "Wallets",
  alerts:           "System Alerts",
  tickets:          "Support Tickets",      // FIXED — was missing, showed "Dashboard"
  notifications:    "Notifications",
  services_settings:"Services Settings",
  general_settings: "App Settings",
  settings:         "Config Settings",
  onboarding_slide: "Onboarding Slides",
  accounts:         "Admin Accounts",
  campaign:         "Marketing",
  dataPlans:        "Data Plans",
  providers:        "VTU Providers",        // ADDED
  coupon:           "Coupons",              // ADDED
  marketing_slides: "Marketing Slides",     // ADDED
  catalog:          "Savings Catalog",      // ADDED
  companies:        "Companies",            // ADDED
  savings_settings: "Savings Settings",     // ADDED
  reward_rules:     "Reward Rules",         // ADDED
  fraud:            "Fraud Holds",          // ADDED
  provider_balances:"Provider Balances",    // ADDED
  audit_logs:       "Audit Logs",           // ADDED
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin:       "Admin",
  support:     "Support",
};

const ROLE_COLORS = {
  super_admin: "#ef4444",
  admin:       "#00C896",
  support:     "#f59e0b",
};

const ALERT_SEVERITY = {
  critical: { color: "text-red-400",   bg: "bg-red-500/10",   icon: "ri-error-warning-fill" },
  warning:  { color: "text-amber-400", bg: "bg-amber-500/10", icon: "ri-alert-fill"          },
  info:     { color: "text-[#00C896]", bg: "bg-[#00C896]/10", icon: "ri-information-fill"    },
};

function timeAgo(date) {
  const m = Math.floor((Date.now() - new Date(date)) / 60_000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler();
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
}

export default function Header({
  activeNav,
  onMenuToggle  = () => {},   // FIXED — default no-op
  alertUnread   = 0,
  alertCritical = 0,
  socketConnected = false,
  alerts        = [],
  onAlertNavigate,
  onAlertDismiss,
}) {
  const { user, logout } = useAuth();
  const [notifOpen,   setNotifOpen]   = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const notifRef   = useRef(null);
  const profileRef = useRef(null);

  useClickOutside(notifRef,   () => setNotifOpen(false));
  useClickOutside(profileRef, () => setProfileOpen(false));

  const adminRole   = user?.adminRole   || "admin";
  const roleLabel   = ROLE_LABELS[adminRole]  || "Admin";
  const roleColor   = ROLE_COLORS[adminRole]  || "#00C896";
  const displayName = user?.firstName
    ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}`
    : user?.email || "Admin";
  const initials = user?.firstName
    ? (user.firstName[0] + (user.lastName?.[0] || "")).toUpperCase()
    : "AD";

  const dropdownAlerts = alerts.slice(0, 8);

  return (
    <header className="h-16 bg-[#0f0f0f] border-b border-white/5 flex items-center justify-between px-4 md:px-6 shrink-0">

      {/* Left — hamburger + page title */}
      <div className="flex items-center gap-3">
        <button onClick={onMenuToggle}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-white/40 hover:bg-white/5 hover:text-white/70 transition-colors cursor-pointer lg:hidden">
          <i className="ri-menu-line text-lg" />
        </button>
        <div>
          <h2 className="text-sm md:text-base font-semibold text-white">
            {NAV_LABELS[activeNav] ?? "Dashboard"}
          </h2>
          <p className="text-[10px] md:text-xs text-white/30 hidden sm:block">
            {new Date().toLocaleDateString("en-NG", {
              weekday: "long", year: "numeric", month: "long", day: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-1 md:gap-2">

        {/* Socket status pill */}
        <div className="hidden md:flex items-center gap-1.5 rounded-full px-3 py-1.5 mr-1 border"
          style={{
            background:   socketConnected ? "rgba(0,200,150,0.08)" : "rgba(245,158,11,0.08)",
            borderColor:  socketConnected ? "rgba(0,200,150,0.2)"  : "rgba(245,158,11,0.2)",
          }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: socketConnected ? "#00C896" : "#f59e0b" }} />
          <span className="text-[11px] font-medium whitespace-nowrap"
            style={{ color: socketConnected ? "#00C896" : "#f59e0b" }}>
            {socketConnected ? "Live" : "Connecting…"}
          </span>
        </div>

        {/* Alert bell */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(v => !v); setProfileOpen(false); }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-white/40 hover:bg-white/5 hover:text-white/70 transition-colors cursor-pointer relative"
          >
            <i className={`ri-notification-3-line text-lg ${alertCritical > 0 ? "text-red-400" : alertUnread > 0 ? "text-amber-400" : ""}`} />
            {alertUnread > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full flex items-center justify-center text-[9px] font-bold px-1 text-white ${alertCritical > 0 ? "bg-red-500" : "bg-amber-500"}`}>
                {alertUnread > 99 ? "99+" : alertUnread}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-11 w-80 bg-[#161616] rounded-2xl border border-white/8 z-50 shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-white">Alerts</span>
                  {alertUnread > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white ${alertCritical > 0 ? "bg-red-500" : "bg-amber-500"}`}>
                      {alertUnread}
                    </span>
                  )}
                </div>
                {onAlertNavigate && (
                  <button
                    onClick={() => { onAlertNavigate("alerts", {}); setNotifOpen(false); }}
                    className="text-[#00C896] text-[10px] font-semibold hover:opacity-70">
                    View all
                  </button>
                )}
              </div>

              <div className="max-h-80 overflow-y-auto divide-y divide-white/4">
                {dropdownAlerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <div className="w-10 h-10 rounded-xl bg-[#00C896]/10 flex items-center justify-center mb-2">
                      <i className="ri-checkbox-circle-line text-[#00C896] text-xl" />
                    </div>
                    <p className="text-white/30 text-xs">No active alerts</p>
                  </div>
                ) : (
                  dropdownAlerts.map(alert => {
                    const sev = ALERT_SEVERITY[alert.severity] || ALERT_SEVERITY.info;
                    return (
                      <div key={alert._id}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-white/3 transition-colors group">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${sev.bg}`}>
                          <i className={`${sev.icon} text-sm ${sev.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold leading-snug">{alert.title}</p>
                          <p className="text-white/40 text-[10px] leading-relaxed mt-0.5 line-clamp-2">{alert.message}</p>
                          <p className="text-white/20 text-[9px] mt-1">{timeAgo(alert.createdAt)}</p>
                        </div>
                        <div className="flex flex-col gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          {alert.link && onAlertNavigate && (
                            <button
                              onClick={() => {
                                const params = new URLSearchParams(alert.link.split("?")[1] || "");
                                onAlertNavigate(params.get("view"), Object.fromEntries(params));
                                setNotifOpen(false);
                              }}
                              className="w-6 h-6 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-white">
                              <i className="ri-arrow-right-up-line text-[10px]" />
                            </button>
                          )}
                          {onAlertDismiss && (
                            <button
                              onClick={() => onAlertDismiss(alert._id)}
                              className="w-6 h-6 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-[#00C896]">
                              <i className="ri-check-line text-[10px]" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {dropdownAlerts.length > 0 && (
                <div className="px-4 py-2.5 border-t border-white/5">
                  <button
                    onClick={() => { onAlertNavigate?.("alerts", {}); setNotifOpen(false); }}
                    className="w-full py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/40 hover:text-white text-xs font-semibold transition-colors">
                    See all alerts
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Admin profile */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => { setProfileOpen(v => !v); setNotifOpen(false); }}
            className="flex items-center gap-2 ml-1 pl-2 md:pl-3 border-l border-white/8 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ background: roleColor + "30", border: `2px solid ${roleColor}40` }}>
              {user?.profileImage
                ? <img src={user.profileImage} className="w-full h-full object-cover rounded-full" alt="" />
                : <span style={{ color: roleColor }}>{initials}</span>}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-white whitespace-nowrap">{displayName}</p>
              <p className="text-[10px] font-medium" style={{ color: roleColor }}>{roleLabel}</p>
            </div>
            <i className={`ri-arrow-down-s-line text-white/30 text-sm hidden sm:block transition-transform ${profileOpen ? "rotate-180" : ""}`} />
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-12 w-52 bg-[#161616] rounded-xl border border-white/8 z-50 shadow-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-white/5">
                <p className="text-white text-sm font-bold truncate">{displayName}</p>
                <p className="text-white/40 text-[10px] truncate">{user?.email}</p>
                <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: roleColor + "20", color: roleColor }}>
                  <i className="ri-shield-star-line text-[10px]" />
                  {roleLabel}
                </span>
              </div>
              <div className="p-1.5">
                <div className="h-px bg-white/5 my-1" />
                <button onClick={() => logout()}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-red-400 hover:bg-red-500/8 transition-colors text-left">
                  <i className="ri-logout-circle-r-line text-base" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
